import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { COLORS } from './colors';
import type { ColorMode } from '../types/content';

// ============================================================
// LexNova MUI Theme
// Dark / Light mode support via ColorModeContext
// ============================================================

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: '"Syne", "Space Grotesk", sans-serif',
  h1: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
  },
  h2: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  h3: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.015em',
  },
  h4: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 600,
  },
  h5: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 600,
  },
  h6: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 600,
  },
  body1: {
    fontFamily: '"DM Sans", sans-serif',
    lineHeight: 1.7,
  },
  body2: {
    fontFamily: '"DM Sans", sans-serif',
    lineHeight: 1.65,
  },
  button: {
    fontFamily: '"Syne", sans-serif',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'none',
  },
};

const sharedComponents = (mode: ColorMode): ThemeOptions['components'] => ({
  MuiCssBaseline: {
    styleOverrides: `
      @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
      * { box-sizing: border-box; }
      html { scroll-behavior: smooth; }
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: ${mode === 'dark' ? COLORS.dark[900] : COLORS.light[200]}; }
      ::-webkit-scrollbar-thumb { background: ${COLORS.purple[500]}; border-radius: 3px; }
    `,
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '50px',
        padding: '10px 28px',
        fontSize: '0.95rem',
        transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(147,51,234,0.35)',
        },
      },
      containedPrimary: {
        background: COLORS.gradients.brand,
        color: '#fff',
        '&:hover': {
          background: COLORS.gradients.brandLight,
        },
      },
      outlinedPrimary: {
        borderColor: COLORS.purple[500],
        color: COLORS.purple[400],
        '&:hover': {
          borderColor: COLORS.magenta[400],
          color: COLORS.magenta[400],
          background: 'rgba(147,51,234,0.06)',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '16px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '8px',
      },
    },
  },
});

export const getTheme = (mode: ColorMode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: COLORS.purple[500],
        light: COLORS.purple[400],
        dark: COLORS.purple[700],
      },
      secondary: {
        main: COLORS.magenta[500],
        light: COLORS.magenta[300],
        dark: COLORS.magenta[600],
      },
      background: {
        default: mode === 'dark' ? COLORS.dark[800] : COLORS.light[100],
        paper: mode === 'dark' ? COLORS.dark[700] : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#f1eeff' : COLORS.gray[900],
        secondary: mode === 'dark' ? COLORS.gray[400] : COLORS.gray[600],
      },
      divider: mode === 'dark' ? COLORS.dark[400] : COLORS.light[400],
    },
    typography: sharedTypography,
    components: sharedComponents(mode),
    shape: { borderRadius: 12 },
  });

export default getTheme;
